import { motion } from 'framer-motion';
import { useState } from 'react';
import { Send, Phone, Mail, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useScrollAnimation, scrollAnimationVariants, transitionPresets } from '@/hooks/useScrollAnimation';

const ContactCTA = () => {
  const { ref, isInView } = useScrollAnimation({ margin: '-80px' as const });
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const form = new FormData();
      form.append('access_key', '00fbe0e9-47f5-4905-8dbb-4777a3fe0d3d');
      form.append('subject', 'Demande de rappel - SAS Rideau');
      form.append('from_name', 'Site SAS Rideau - Accueil');
      form.append('nom', formData.name);
      form.append('societe', formData.company);
      form.append('email', formData.email);
      form.append('telephone', formData.phone);
      form.append('botcheck', '');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: form,
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
        toast({
          title: t('contactCta.toastTitle'),
          description: t('contactCta.toastDesc'),
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue. Veuillez réessayer.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactItems = [
    { icon: Phone, label: t('contactCta.phone'), value: '+33 4 56 19 00 53', href: 'tel:+33456190053' },
    { icon: Mail, label: t('contactCta.email'), value: 'info@societerideau.fr', href: 'mailto:info@societerideau.fr' },
  ];

  return (
    <section ref={ref} className="section-padding bg-gradient-ocean relative overflow-hidden z-10">
      {/* Animated pattern overlay */}
      <motion.div 
        className="absolute inset-0 pattern-overlay opacity-30"
        animate={{
          backgroundPosition: ['0% 0%', '100% 100%'],
        }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
      />
      
      <div className="container-premium relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content with reveal animation */}
          <motion.div
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            variants={scrollAnimationVariants.staggerContainer}
          >
            <motion.span 
              variants={scrollAnimationVariants.fadeUp}
              transition={{ duration: 0.8 }}
              className="text-accent font-medium tracking-wider uppercase text-sm block"
            >
              {t('contactCta.label')}
            </motion.span>
            
            <motion.h2 
              variants={scrollAnimationVariants.fadeUp}
              transition={{ duration: 0.8 }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mt-4 mb-6"
            >
              {t('contactCta.title')}
            </motion.h2>
            
            <motion.div 
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="section-divider !mx-0 mb-8 origin-left" 
            />
            
            <motion.p 
              variants={scrollAnimationVariants.fadeUp}
              transition={{ duration: 0.8 }}
              className="text-foreground/80 leading-relaxed mb-8 max-w-lg"
            >
              {t('contactCta.description')}
            </motion.p>

            <motion.div 
              variants={scrollAnimationVariants.staggerContainer}
              className="space-y-4"
            >
              {contactItems.map((item, index) => (
                <motion.a
                  key={index}
                  href={item.href}
                  variants={scrollAnimationVariants.staggerItem}
                  transition={{ duration: 0.5 }}
                  whileHover={{ x: 8 }}
                  className="flex items-center gap-4 text-foreground/80 cursor-pointer group"
                >
                  <motion.div 
                    className="p-3 bg-accent/20 rounded-lg"
                    whileHover={{ 
                      scale: 1.1, 
                      rotate: 5,
                      backgroundColor: 'hsl(var(--accent) / 0.3)' 
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <item.icon className="text-accent" size={20} />
                  </motion.div>
                  <div>
                    <p className="text-sm text-muted-foreground">{item.label}</p>
                    <p className="font-medium group-hover:text-accent transition-colors duration-300">
                      {item.value}
                    </p>
                  </div>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          {/* Form with 3D entrance */}
          <motion.div
            initial={{ opacity: 0, x: 80, rotateY: -10 }}
            animate={isInView ? { opacity: 1, x: 0, rotateY: 0 } : {}}
            transition={{ 
              duration: 1, 
              delay: 0.3,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            style={{ transformStyle: 'preserve-3d', perspective: '1000px' }}
          >
            <motion.div 
              className="bg-card/90 backdrop-blur-md rounded-2xl p-8 md:p-10 shadow-elegant border border-border"
              whileHover={{ 
                boxShadow: '0 30px 60px -15px rgba(0, 0, 0, 0.3)',
              }}
              transition={{ duration: 0.4 }}
            >
              {isSubmitted ? (
                <motion.div 
                  className="text-center py-8"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                >
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                  >
                    <CheckCircle className="mx-auto text-accent mb-4" size={48} />
                  </motion.div>
                  <motion.h3 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="font-serif text-2xl font-semibold text-foreground mb-2"
                  >
                    {t('contactCta.successTitle')}
                  </motion.h3>
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-muted-foreground"
                  >
                    {t('contactCta.successDesc')}
                  </motion.p>
                </motion.div>
              ) : (
                <motion.form 
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  initial="hidden"
                  animate={isInView ? 'visible' : 'hidden'}
                  variants={scrollAnimationVariants.staggerContainer}
                >
                  <motion.h3 
                    variants={scrollAnimationVariants.fadeUp}
                    className="font-serif text-2xl font-semibold text-foreground mb-6"
                  >
                    {t('contactCta.formTitle')}
                  </motion.h3>
                  
                  <motion.div 
                    variants={scrollAnimationVariants.fadeUp}
                    className="grid sm:grid-cols-2 gap-4"
                  >
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        {t('contactCta.fullName')} *
                      </label>
                      <Input
                        required
                        placeholder="Jean Dupont"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-background/50 border-border focus:border-accent transition-all duration-300"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        {t('contactCta.company')}
                      </label>
                      <Input
                        placeholder="Votre entreprise"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="bg-background/50 border-border focus:border-accent transition-all duration-300"
                      />
                    </div>
                  </motion.div>

                  <motion.div variants={scrollAnimationVariants.fadeUp}>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      {t('contactCta.emailField')} *
                    </label>
                    <Input
                      type="email"
                      required
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-background/50 border-border focus:border-accent transition-all duration-300"
                    />
                  </motion.div>

                  <motion.div variants={scrollAnimationVariants.fadeUp}>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      {t('contactCta.phoneField')} *
                    </label>
                    <Input
                      type="tel"
                      required
                      placeholder="+33 6 00 00 00 00"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-background/50 border-border focus:border-accent transition-all duration-300"
                    />
                  </motion.div>

                  <motion.div
                    variants={scrollAnimationVariants.fadeUp}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button type="submit" variant="gold" size="lg" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? '...' : t('contactCta.submit')}
                      <Send size={18} />
                    </Button>
                  </motion.div>

                  <motion.p 
                    variants={scrollAnimationVariants.fadeUp}
                    className="text-xs text-muted-foreground text-center"
                  >
                    {t('contactCta.consent')}
                  </motion.p>
                </motion.form>
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactCTA;
